// src/pages/activitylevel.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase, currentUser } from "@/lib/supabase";
import { markCompleted, getStepIndexById, steps } from "@/lib/onboarding";

const levels = [
  "Sedentary",
  "Lightly Active",
  "Moderately Active",
  "Very Active",
  "Extremely Active",
];

export default function ActivityLevel() {
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();

  const handleNext = async () => {
    if (!sel) return;
    setSaving(true);
    setErr(null);
    try {
      const user = await currentUser();
      if (!user) throw new Error("No user found — please log in again.");

      const supabase = getSupabase();
      if (!supabase) throw new Error("Supabase client not initialized.");

      const { error } = await supabase
        .from("clients")
        .update({ activity_level: sel })
        .eq("user_id", user.id);

      if (error) throw error;

      markCompleted("activityLevel");
      const nextIdx = getStepIndexById("activityLevel") + 1;
      const nextPath = steps[nextIdx]?.path || "/primary-sport";
      nav(nextPath);
    } catch (e: any) {
      setErr(e.message ?? "Failed to save activity level.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout>
      <div className="absolute top-4 left-4 z-10">
  <button
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#0f172a] transition-colors"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
</div>
      
      <div className="space-y-6 text-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f172a]">Activity Level</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-2">
            Select your usual level of daily activity.
          </p>
        </div>

        {/* Pill Buttons */}
        <div className="space-y-3">
          {levels.map((l) => (
            <button
              key={l}
              onClick={() => setSel(l)}
              className={`
                w-full h-14 rounded-full text-base font-semibold transition-all duration-200
                flex items-center justify-center
                ${sel === l
                  ? "bg-[#a78bfa] text-[#0f172a] shadow-lg shadow-[#a78bfa]/30"
                  : "bg-gray-800 text-[#0f172a] border border-gray-700 hover:bg-gray-700"
                }
                active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#a78bfa]/50
              `}
            >
              {l}
            </button>
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={!sel || saving}
          className={`
            w-full bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-semibold
            rounded-2xl py-3 transition-all duration-200
            active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed
            ${!sel || saving ? "opacity-60" : ""}
          `}
        >
          {saving ? "Saving..." : "Next"}
        </button>

        {err && <p className="text-sm text-red-400">{err}</p>}

        <div className="my-6 border-t border-gray-800" />

        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
          Input
        </div>

        <footer className="text-center text-xs text-gray-500">
          © 2025 Input. All rights reserved.
        </footer>

        <div className="mt-6">
          <ProgressBar step={4} total={10} />
        </div>
      </div>
    </AuthLayout>
  );
}
